import { SearchAndFilterDisplayOptionsModel } from 'models';

import { isPriceSliderFilter } from 'utils/price-filter-utils';

import { LodgingPwaPropertySearch } from '__generated__/api';
import { PillProps } from 'components/search/playback/playback-filter-pill';

const validFields = [
  'sort',
  'hotelName',
  'commissionTier',
  'agencyBusinessModel',
  'rewards',
  'popularFilter',
  'star',
  'price',
  'guestRating',
  'cleaningAndSafetyPractices',
  'paymentType',
  'propertyStyle',
  'lodging',
  'bedroomFilter',
  'regionId',
  'neighborhood',
  'poi',
  'mealPlan',
  'amenities',
  'accessibility',
  'travelerType',
  'deals',
];

export const createSearchAndFilterDisplayOptions = (
  sortAndFilter?: LodgingPwaPropertySearch.SortAndFilter
): SearchAndFilterDisplayOptionsModel => {
  if (!sortAndFilter) {
    return {
      filters: {
        displayFields: validFields,
      },
    };
  }

  return {
    filters: {
      displayFields: sortAndFilter.options.sortAndFilter.filter(option => validFields.includes(option.title)).map(option => option.title),
    },
  };
};

export enum SortAndFilterDisplayField {
  SORT = 'sort',
  HOTEL_NAME = 'hotelName',
  REWARDS = 'rewards',
  STAR = 'star',
  PRICE = 'price',
  GUEST_RATING = 'guestRating',
  CLEANING_AND_SAFETY = 'cleaningAndSafetyPractices',
  LODGING = 'lodging',
  REGION_ID = 'regionId',
  MEAL_PLAN = 'mealPlan',
  AMENITIES = 'amenities',
  TRAVELER_TYPE = 'travelerType',
  PAYMENT_TYPE = 'paymentType',
  DEALS = 'deals',
  PROPERTY_STYLE = 'propertyStyle',
  BEDROOM_FILTER = 'bedroomFilter',
  NEIGHBORHOOD = 'neighborhood',
}

export const mapSortAndFilter = (sortAndFilter: LodgingPwaPropertySearch.SortAndFilter): any =>
  sortAndFilter
    ? {
        defaults: getDefaults(sortAndFilter),
        options: getOptions(sortAndFilter),
        pills: getPills(sortAndFilter),
      }
    : null;

export const getSortAndFilterOption = (
  field: SortAndFilterDisplayField,
  sortAndFilter: any
): LodgingPwaPropertySearch._SortAndFilter | null => {
  if (sortAndFilter && sortAndFilter.options) {
    const result = sortAndFilter.options[field];

    return result ? result : null;
  }

  return null;
};

const getDefaults = (sortAndFilter: LodgingPwaPropertySearch.SortAndFilter): any => ({
  neighborhood: sortAndFilter && sortAndFilter.defaults && sortAndFilter.defaults.regionId,
  sort: sortAndFilter && sortAndFilter.defaults && sortAndFilter.defaults.sort,
});

const getOptions = (sortAndFilter: LodgingPwaPropertySearch.SortAndFilter): any => {
  const appliedFilterCount = sortAndFilter && sortAndFilter.options && sortAndFilter.options.appliedFilterCount;
  const sortAndFilterOptions = sortAndFilter && sortAndFilter.options && mapSortAndFilterOptions(sortAndFilter.options.sortAndFilter);

  return Object.assign({}, { appliedFilterCount }, sortAndFilterOptions);
};

const mapSortAndFilterOptions = (sortAndFilterOptions: LodgingPwaPropertySearch._SortAndFilter[]): any =>
  Object.assign(
    {},
    ...sortAndFilterOptions.map(sortAndFilterOption => ({
      [sortAndFilterOption.title]: {
        options: sortAndFilterOption.options.map(mapOption),
        label: sortAndFilterOption.label,
        max: sortAndFilterOption.max,
        min: sortAndFilterOption.min,
        range: sortAndFilterOption.isRange,
        placeholder: sortAndFilterOption.placeholder,
        subLabel: sortAndFilterOption.subLabel,
        step: sortAndFilterOption.step,
        name: sortAndFilterOption.name,
        hiddenThreshold: sortAndFilterOption.hiddenThreshold,
        title: sortAndFilterOption.title,
      },
    }))
  );

const mapOption = (option: LodgingPwaPropertySearch._Options): any => ({
  selected: option.isSelected,
  ...option,
  value: option.optionValue,
});

const getPills = (filters: LodgingPwaPropertySearch.SortAndFilter): PillProps[] => {
  const selectedFilters = filters?.options?.sortAndFilter?.filter(filter => filter.selected);

  return [].concat(
    ...selectedFilters.map((filter: LodgingPwaPropertySearch._SortAndFilter) =>
      getPillsFromFilter(filter, filters.selected.priceFilterPills)
    )
  );
};

const getPillsFromFilter = (
  filter: LodgingPwaPropertySearch._SortAndFilter,
  pricePills: LodgingPwaPropertySearch.PriceFilterPills
): PillProps[] => {
  const { title, min, max } = filter;
  if (title === 'price' && !isPriceSliderFilter(min, max)) {
    return [getPriceBucketsFilterPill(pricePills)];
  }

  return filter?.options?.filter(option => option.isSelected).map(option => getPill(filter, option));
};

const getPill = (filter: LodgingPwaPropertySearch._SortAndFilter, option: LodgingPwaPropertySearch._Options): PillProps => ({
  query: '',
  name: filter.title,
  label: option.selectedLabel,
  value: option.optionValue,
  generated: true,
});

const getPriceBucketsFilterPill = (pricePills: LodgingPwaPropertySearch.PriceFilterPills): PillProps => ({
  query: '',
  name: 'price',
  label: pricePills && pricePills.aggregate.label,
  value: pricePills && pricePills.aggregate.value,
  generated: true,
});
